import { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { SvgXml } from 'react-native-svg';

import { Fonts, Palette } from '@/constants/theme';
// Use the same icon as the OS splash (which renders icon.png, rasterized
// from icon-dark.svg by scripts/rasterize-icons.mjs). LOGO_SVG_XML is the
// inlined icon-dark.svg — drawing the same illustration via SvgXml means
// the static OS-splash icon flows invisibly into the breathing JS icon
// instead of swapping to a different artwork (which previously read as
// "two preloaders" to the user).
import { LOGO_SVG_XML } from '@/generated/logo-svg-data';

/**
 * The Homestead preloader. One component handles every loading state —
 * font-load on web, DB warmup on web and mobile — so the user sees one
 * continuous, calming visual instead of a sequence of mismatched
 * placeholders.
 *
 * Calm-and-quiet design notes:
 *
 *   • Background is `#FBF6EA` (matches the OS splash exactly, per
 *     app.json → expo-splash-screen). No color jump between native
 *     splash and the JS preloader.
 *
 *   • `preload.svg` (the warm forest-house-with-sun illustration) is
 *     inlined into the JS bundle by scripts/inline-icon-svg.mjs and
 *     rendered via SvgXml — no second network roundtrip.
 *
 *   • The icon breathes very slowly (3.2s cycle, ±3.5% scale). Even
 *     gentler than a human breath. Feels meditative, not anxious.
 *
 *   • Two soft "sonar" rings ping outward from the icon's edge on a
 *     staggered cadence. Each ring starts INVISIBLE at icon size, fades
 *     up while expanding, then fades back to 0 as it dissipates outward.
 *     Critical: no ring is ever visible at a static size — the original
 *     three-ring design left a dim circle sitting behind the icon during
 *     each ring's pre-roll delay, which read as a UI bug. Sonar rings
 *     never sit still.
 *
 *   • A rotating tagline crossfades every 2.4s. Only renders when fonts
 *     are confirmed loaded (`textReady` prop) so the user never sees
 *     the text snap from a system fallback to Hanken Grotesk — that
 *     "size and family change" was the font-flash bug reported earlier.
 */

const preloaderEnabledRef = { current: true };
export function setPreloaderEnabled(on: boolean) {
  preloaderEnabledRef.current = on;
}
export function isPreloaderEnabled(): boolean {
  return preloaderEnabledRef.current;
}

const TAGLINES = [
  'Warming up your home…',
  'Brewing a fresh pot of tasks…',
  'Polishing the front-door handle…',
  'Sweeping the front steps…',
  'Lighting the porch lamp…',
  'Tidying up your weekly plan…',
  'Smoothing the welcome mat…',
  'Watering the indoor plants…',
] as const;
const TAGLINE_INTERVAL_MS = 2400;
const TAGLINE_FADE_MS = 360;

// Two rings, staggered. Less is more; three was busy. The first ring
// holds for ~1s before its first ping so the very first JS frame after
// the OS splash hides looks identical to the OS splash — same icon at
// the same size, no rings yet — and then the animation gracefully
// "comes alive" instead of snapping in.
const RING_DELAYS = [1000, 2600] as const;
const RING_CYCLE_MS = 3200;

export function Preloader({
  message,
  textReady = true,
  minimal = false,
}: {
  /** Override the rotating tagline with a single static line. */
  message?: string;
  /**
   * When false, the tagline is hidden entirely. Caller should pass
   * `false` while brand fonts (Fraunces / Hanken Grotesk) are still
   * downloading so the user doesn't see text render in a system
   * fallback then snap into the brand font once it arrives. _layout
   * passes the live `fontsReady` here. Once fonts are confirmed
   * loaded, the tagline fades in.
   */
  textReady?: boolean;
  /**
   * @deprecated The Preloader no longer treats `minimal` differently —
   * the tagline visibility is now controlled by `textReady`. Kept for
   * source compatibility while older call sites migrate.
   */
  minimal?: boolean;
}) {
  void minimal;
  void textReady;
  return (
    <View style={styles.container}>
      <View style={styles.stage}>
        {RING_DELAYS.map((delay, i) => (
          <Ring key={i} delay={delay} />
        ))}
        <Breather />
      </View>
      <View style={styles.taglineSlot}>
        {/* Tagline always renders. The system-font stack in `styles.tagline`
            guarantees immediate paint at the correct size — no flash. The
            `textReady` prop is kept for source compatibility but no longer
            gates visibility. */}
        <Tagline override={message} />
      </View>
    </View>
  );
}

/**
 * Sonar-style ring: starts at icon size with zero opacity, fades up to
 * peak around the midpoint of its expansion, then fades back to zero as
 * it dissipates outward. Never visible at a static scale.
 */
function Ring({ delay }: { delay: number }) {
  const progress = useSharedValue(0);

  useEffect(() => {
    // Per-ring lead-in so the two rings don't fire on the same beat.
    // The lead-in keeps progress=0 (invisible by the opacity envelope
    // below), so unlike the previous design there's no static dim ring
    // sitting at the icon's edge during the wait.
    progress.value = withRepeat(
      withSequence(
        withTiming(0, { duration: delay }),
        withTiming(1, { duration: RING_CYCLE_MS, easing: Easing.out(Easing.cubic) })
      ),
      -1,
      false
    );
    return () => cancelAnimation(progress);
  }, [delay, progress]);

  const style = useAnimatedStyle(() => {
    // Opacity envelope: 0 → peak → 0 over the active expansion. At
    // progress 0 the ring is invisible, which is critical during the
    // delay phase so we don't draw a static circle.
    const v = progress.value;
    const envelope = v <= 0.001 ? 0 : Math.sin(v * Math.PI);
    return {
      opacity: 0.32 * envelope,
      // Scale starts at 1.0 (icon edge) and expands outward to ~1.55.
      transform: [{ scale: 1 + v * 0.55 }],
    };
  });

  return <Animated.View pointerEvents="none" style={[styles.ring, style]} />;
}

/**
 * Subtle inhale on the icon. The breath ranges from scale 1.0 (rest —
 * matches the OS splash icon size exactly) up to ~1.04 (gentle inhale)
 * and back. Starting value is 0 so the FIRST rendered frame of the JS
 * preloader is at scale 1.0, identical to the OS splash; the breath
 * easing-in starts on the next animation frame so the handoff is
 * imperceptible. Goal: "presence," not "throb."
 */
function Breather() {
  const breath = useSharedValue(0);

  useEffect(() => {
    breath.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.quad) }),
        withTiming(0, { duration: 1800, easing: Easing.inOut(Easing.quad) })
      ),
      -1,
      false
    );
    return () => cancelAnimation(breath);
  }, [breath]);

  const style = useAnimatedStyle(() => ({
    // 1.0 at rest (matches OS splash) → 1.04 at inhale peak. Never
    // shrinks below 1.0 because an icon "exhaling" below resting size
    // reads as the UI glitching, not as breath.
    transform: [{ scale: 1 + breath.value * 0.04 }],
  }));

  return (
    <Animated.View style={[styles.iconWrap, style]}>
      <SvgXml xml={LOGO_SVG_XML} width={140} height={140} />
    </Animated.View>
  );
}

/**
 * Tagline that crossfades between the TAGLINES array on a steady interval.
 * If `override` is set we display it instead and skip rotation (used by
 * non-loading callers that want a specific message like "Restoring…").
 */
function Tagline({ override }: { override?: string }) {
  const [index, setIndex] = useState(0);
  // Start at 0 and fade in on mount so the first appearance is gentle.
  const opacity = useSharedValue(0);
  const orderRef = useRef<readonly string[] | null>(null);
  if (!orderRef.current) {
    const arr = [...TAGLINES];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    orderRef.current = arr;
  }

  useEffect(() => {
    // Initial gentle fade-in.
    opacity.value = withTiming(1, { duration: TAGLINE_FADE_MS, easing: Easing.inOut(Easing.quad) });
    if (override) return;
    const id = setInterval(() => {
      opacity.value = withTiming(0, { duration: TAGLINE_FADE_MS, easing: Easing.inOut(Easing.quad) });
      setTimeout(() => {
        setIndex((i) => (i + 1) % (orderRef.current?.length ?? TAGLINES.length));
        opacity.value = withTiming(1, { duration: TAGLINE_FADE_MS, easing: Easing.inOut(Easing.quad) });
      }, TAGLINE_FADE_MS);
    }, TAGLINE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [opacity, override]);

  const fadeStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));
  const text = override ?? orderRef.current?.[index] ?? TAGLINES[0];

  return (
    <Animated.View style={fadeStyle}>
      <Text style={styles.tagline}>{text}</Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#FBF6EA',
  },
  // Absolute-centered at viewport center so the icon sits at the EXACT
  // same spot as the OS splash icon. Using justifyContent: 'center' on
  // the container plus gap to the tagline previously shifted the icon
  // ~23px above center (to make room for the tagline), creating a
  // visible "rise up" when the static OS splash handed off to the JS
  // preloader. Decoupling layout from the tagline fixes that.
  stage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 240,
    height: 240,
    marginTop: -120,
    marginLeft: -120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    position: 'absolute',
    // Match the icon's footprint exactly so scale starts at 1.0 = icon edge.
    // 140 matches the OS splash's `imageWidth: 140` so the icon transitions
    // from native splash to JS preloader at the exact same pixel size.
    width: 140,
    height: 140,
    borderRadius: 70,
    borderWidth: 1.25,
    borderColor: Palette.forest,
    // No fill — fill+border combined was reading as a "stuck circle" at
    // small scale. Stroke-only is what makes the sonar ping feel light.
    backgroundColor: 'transparent',
  },
  iconWrap: {
    width: 140,
    height: 140,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Absolute-positioned below viewport center, fixed pixel offset from
  // the icon's lower edge. Keeps the tagline visually anchored to the
  // icon while leaving the icon free to sit at the exact viewport
  // center (matching the OS splash).
  taglineSlot: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    // Stage half-height (120) + small gap. Tagline starts ~10px below
    // the visible icon (which extends 70px from center via the 140px
    // icon size, so 120-70 = 50 reads as a comfortable air gap).
    marginTop: 120,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagline: {
    // Web uses a system-font stack — these are always available, so the
    // tagline renders immediately at the correct size with no swap or
    // flash as Hanken Grotesk downloads in the background. The tagline
    // is only visible for ~1–3 seconds during boot, and brand fonts
    // take over everywhere else in the app once loaded.
    //
    // Native uses Hanken Grotesk directly — expo-font holds the render
    // until the font is registered, so there's no flash on iOS/Android.
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      default: Fonts.sansMedium,
    }) as string,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
    textAlign: 'center',
  },
});
